/**
 * useArtifactCapabilityAdvisory
 *
 * Fetches capability map rows for an artifact and resolves human-readable labels.
 * Drives Level 1 (advisory panel) and Level 2 (soft gate) for training access rules.
 *
 * Only returns entries with weight >= 1.0 (meaningful signal, not noise).
 * Sorted: is_primary DESC, weight DESC.
 */

import { useMemo } from 'react';
import { useArtifactCapabilityMappings, useTrainingCapabilities } from '@/hooks/useTrainingCapabilities';

export interface CapabilityAdvisoryEntry {
  key: string;
  label: string;
  is_primary: boolean;
  weight: number;
}

export function useArtifactCapabilityAdvisory(artifactId: string | null | undefined) {
  const { data: mappings = [], isLoading: mappingsLoading } = useArtifactCapabilityMappings(
    artifactId ?? null
  );
  const { data: capabilities = [], isLoading: capsLoading } = useTrainingCapabilities();

  const advisoryEntries = useMemo<CapabilityAdvisoryEntry[]>(() => {
    if (!mappings.length || !capabilities.length) return [];

    const labelMap = new Map(capabilities.map((c) => [c.key, c.label]));

    return mappings
      .filter((m) => m.weight >= 1.0)
      .map((m) => ({
        key: m.capability_key,
        label: labelMap.get(m.capability_key) ?? m.capability_key,
        is_primary: m.is_primary,
        weight: m.weight,
      }))
      .sort((a, b) => {
        if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
        return b.weight - a.weight;
      });
  }, [mappings, capabilities]);

  return {
    advisoryEntries,
    isLoading: mappingsLoading || capsLoading,
  };
}
